import { auth } from './firebase';
import { workspaceHeader } from './workspaces';

export interface FormTemplateData {
  id?: string;
  templateName: string;
  title: string;
  description: string;
  instructions?: string;
  responseType: string;
  priority: string;
  requiredDocs?: string[];
  dropdownOptions?: string[];
  formFields?: any[];
  createdAt?: string;
}

const STORAGE_KEY = 'special_request_form_templates';

function getLocalTemplates(): FormTemplateData[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function setLocalTemplates(templates: FormTemplateData[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
  } catch {}
}

export async function fetchFormTemplates(): Promise<FormTemplateData[]> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      const res = await fetch('/api/admin/special-request-templates', {
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.templates && Array.isArray(data.templates)) {
          setLocalTemplates(data.templates);
          return data.templates;
        }
      }
    }
  } catch (err) {
    console.warn('Failed to fetch templates from API, falling back to localStorage:', err);
  }
  return getLocalTemplates();
}

export async function saveFormTemplate(template: FormTemplateData): Promise<FormTemplateData> {
  const localList = getLocalTemplates();
  const fallbackObj: FormTemplateData = {
    ...template,
    id: template.id || 'tmpl_' + Date.now(),
    createdAt: new Date().toISOString()
  };

  try {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      const res = await fetch('/api/admin/special-request-templates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...workspaceHeader() },
        body: JSON.stringify(template)
      });
      if (res.ok) {
        const data = await res.json();
        if (data.template) {
          const updated = [data.template, ...localList.filter(t => t.id !== data.template.id)];
          setLocalTemplates(updated);
          return data.template;
        }
      }
    }
  } catch (err) {
    console.warn('API save template failed, using local storage:', err);
  }

  const updated = [fallbackObj, ...localList.filter(t => t.id !== fallbackObj.id)];
  setLocalTemplates(updated);
  return fallbackObj;
}

export async function deleteFormTemplate(id: string): Promise<boolean> {
  try {
    const token = await auth.currentUser?.getIdToken();
    if (token) {
      await fetch(`/api/admin/special-request-templates/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, ...workspaceHeader() }
      });
    }
  } catch (err) {
    console.warn('API delete template failed:', err);
  }
  const localList = getLocalTemplates().filter(t => t.id !== id);
  setLocalTemplates(localList);
  return true;
}
